/**
 * Uniform arrays that hold a Godot term for each of three's lights of a kind, by the light's slot,
 * for every lit program. They grow with the scene's lights, and a program keeps the arrays it
 * compiled with, so each write reaches every set handed out.
 */
import { installedUniformValue, installLitUniform } from './litUniform.js';
/** Room for this many lights before the first growth. */
const INITIAL_CAPACITY = 4;
export class LightUniformArrays {
    floatsPerLight;
    /**
     * Every set handed to the lit materials, oldest first. A material clones the newest set when it
     * compiles a program, and growth comes before any program with more lights, so each program reads
     * no further than its set holds. Written only by `growTo`.
     */
    sets;
    /** The uniform names, read once: a write runs on every shadow pass, so it allocates no list. */
    names;
    /** `floatsPerLight` names each uniform and the floats it holds for one light. */
    constructor(floatsPerLight) {
        this.floatsPerLight = floatsPerLight;
        this.names = Object.keys(floatsPerLight);
        this.sets = [this.installedSet() ?? this.createSet(INITIAL_CAPACITY)];
    }
    /** The arrays the next program to compile reads. */
    newest() {
        return this.sets[this.sets.length - 1];
    }
    /** Gives the newest arrays to every built-in lit material, for every material created after the call. */
    install() {
        for (const [name, array] of this.entries(this.newest()))
            installLitUniform(name, array);
    }
    /**
     * Grows the arrays to hold `count` lights, lets `fill` write the newest set, then copies the
     * first `count` lights into each older set as far as it holds.
     */
    write(count, fill) {
        this.growTo(count);
        const newest = this.newest();
        fill(newest);
        for (const set of this.sets) {
            if (set === newest)
                continue;
            for (const name of this.names) {
                const length = Math.min(count * this.floatsPerLight[name], set[name].length);
                set[name].set(newest[name].subarray(0, length));
            }
        }
    }
    growTo(count) {
        const name = this.names[0];
        const capacity = this.newest()[name].length / this.floatsPerLight[name];
        if (count <= capacity)
            return;
        this.sets.push(this.createSet(Math.max(count, capacity * 2)));
        this.install();
    }
    createSet(capacity) {
        const set = {};
        for (const name of this.names)
            set[name] = new Float32Array(capacity * this.floatsPerLight[name]);
        return set;
    }
    /** The arrays an earlier evaluation of the caller gave the lit materials, as after a dev server reloads it. */
    installedSet() {
        const set = {};
        for (const name of this.names) {
            const array = installedUniformValue(name);
            if (!(array instanceof Float32Array))
                return null;
            set[name] = array;
        }
        return set;
    }
    entries(set) {
        return this.names.map((name) => [name, set[name]]);
    }
}
//# sourceMappingURL=lightUniformArrays.js.map